import { ThunkApiConfig } from '@/models/ThunkApiConfig';
import apiInstance from '@api/index';
import toastMessage from '@components/Toast';
import { StartedTaskResponse } from '@pages/RunDeliverable/store/types';
import { rememberStartedTask } from '@/store/backgroundTasks/startedTasks';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { onModalCloseRequest } from '@store/modal/thunks';
import { isDef } from '@utils/def';

import { ConfirmModals } from '../components/DeliverablesList/components/Actions/enum';
import { LogMessages } from '../enums';
import { ActivityLogParams, fetchUploadedFilesPayload } from '../types';

import {
  CloneScheduledDeliverablesRequestPayload,
  CreateUpdateScheduledDeliverableRequestPayload,
  DeleteDeliverableRequestPayload,
  DeleteDeliverablesRequestPayload,
  DeleteReportDownloadRequestPayload,
  GetActivityLogsRequestPayload,
  GetClientEmailsRequestPayload,
  GetReportListDownloadRequestPayload,
  GetScheduledDeliverableRequestPayload,
  GetScheduledDeliverableTransactionsRequestPayload,
  GetScheduledDeliverablesTransactionsRequestPayload,
  RerunValidationsRequestPayload,
  RunDeliverableReportRequestPayload,
  SendClientEmailsRequestPayload,
  SetMarkAsCompletedRequestPayload,
  cloneScheduledDeliverable,
  createUpdateScheduledDeliverable,
  deleteDeliverable,
  deleteDeliverables,
  deleteReportDownload,
  getActivityLogs,
  getClientEmails,
  getNewTrades,
  getReportListDownload,
  getScheduledDeliverable,
  getScheduledDeliverableTransactions,
  getScheduledDeliverablesList,
  getScheduledDeliverablesTestReport,
  getScheduledDeliverablesTestReports,
  getScheduledDeliverablesTransactions,
  rerunValidations,
  runDeliverableReport,
  sendClientEmails,
  setMarkAsCompleted,
} from './actions';

export const fetchScheduledDeliverablesList =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchScheduledDeliverablesList',
    async (_, { dispatch, rejectWithValue }) => {
      dispatch(getScheduledDeliverablesList.request());

      try {
        toastMessage.info('Loading Scheduled Deliverables...');
        const response =
          await apiInstance.scheduledDeliverables.fetchScheduledDeliverablesAll();
        toastMessage.success('Scheduled Deliverables loaded successfully');
        dispatch(
          getScheduledDeliverablesList.success({ data: response.data.data }),
        );
      } catch (error: any) {
        dispatch(getScheduledDeliverablesList.failure());
        return rejectWithValue(
          error.message ||
            'An unknown error occurred in fetchScheduledDeliverablesAll request',
        );
      }
    },
  );
export const fetchScheduledDeliverable =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchScheduledDeliverablesList',
    async (
      { schedule_id }: GetScheduledDeliverableRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(getScheduledDeliverable.request({ schedule_id }));

      try {
        toastMessage.info(`Loading Scheduled Deliverable #${schedule_id}`);
        const response =
          await apiInstance.scheduledDeliverables.getScheduledDeliverable({
            schedule_id,
          });
        toastMessage.success(
          `Scheduled Deliverable #${schedule_id} loaded successfully`,
        );
        dispatch(getScheduledDeliverable.success({ data: response.data.data }));
      } catch (error: any) {
        dispatch(getScheduledDeliverable.failure({ schedule_id }));
        return rejectWithValue(
          error.message ||
            'An error occurred while fetching Scheduled Deliverable.',
        );
      }
    },
  );
export const markScheduledCompleted =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/markScheduledCompleted',
    async (
      { schedule_id, journal_entries_status }: SetMarkAsCompletedRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      const modalId = `${ConfirmModals.Completed}_${schedule_id}`;
      try {
        dispatch(
          setMarkAsCompleted.request({ schedule_id, journal_entries_status }),
        );
        toastMessage.info(
          `Marking Scheduled Deliverable #${schedule_id} as Complete...`,
        );
        const response =
          await apiInstance.scheduledDeliverables.markScheduledDeliverableCompleted(
            {
              schedule_id: schedule_id,
              journal_entries_status: journal_entries_status,
            },
          );
        if (response.data.success) {
          toastMessage.success(
            `Scheduled Deliverable #${schedule_id} marked as Completed!`,
          );
          dispatch(setMarkAsCompleted.success());
          dispatch(fetchScheduledDeliverablesList());
        } else {
          toastMessage.error(
            `Unable to Mark Scheduled Deliverable #${schedule_id} as Complete`,
          );
        }
        dispatch(onModalCloseRequest({ id: modalId }));
      } catch (error: any) {
        dispatch(setMarkAsCompleted.failure());
        const errorMsg =
          error.message || 'An error occured while marking as Completed!';
        toastMessage.error(errorMsg);
        return rejectWithValue(errorMsg);
      }
    },
  );
export const deleteScheduledDeliverable =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/deleteScheduledDeliverable',
    async (
      { schedule_id, modalId }: DeleteDeliverableRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      const deleteModalId = modalId || `${ConfirmModals.Delete}_${schedule_id}`;
      try {
        dispatch(deleteDeliverable.request({ schedule_id }));
        toastMessage.info(`Deleting Scheduled Deliverable #${schedule_id}`);
        const response =
          await apiInstance.scheduledDeliverables.deleteScheduledDeliverable({
            schedule_id,
          });
        if (response.data.success) {
          toastMessage.success(
            `Scheduled Deliverable #${schedule_id} Deleted!`,
          );
          dispatch(deleteDeliverable.success());
          dispatch(fetchScheduledDeliverablesList());
        } else {
          toastMessage.error(
            `Scheduled Deliverable #${schedule_id} Deleting FAILED!`,
          );
        }
        dispatch(onModalCloseRequest({ id: deleteModalId }));
      } catch (error: any) {
        dispatch(deleteDeliverable.failure());
        const errorMsg =
          error.message ||
          'An error occurs while deleting the Scheduled Deliverable!';
        toastMessage.error(errorMsg);
        dispatch(onModalCloseRequest({ id: deleteModalId }));
        return rejectWithValue(errorMsg);
      }
    },
  );
export const deleteScheduledDeliverables =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/deleteScheduledDeliverables',
    async (
      {
        selected_schedule_ids,
        schedule_id,
        modalId,
      }: DeleteDeliverablesRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      const deleteModalId =
        modalId || `${ConfirmModals.DeleteSelected}_${schedule_id}`;
      try {
        dispatch(
          deleteDeliverables.request({ selected_schedule_ids, schedule_id }),
        );
        toastMessage.info('Deleting Scheduled Deliverables...');
        const response =
          await apiInstance.scheduledDeliverables.deleteScheduledDeliverables({
            selected_schedule_ids,
          });
        if (response.data.success) {
          toastMessage.success('Scheduled Deliverables Were Deleted!');
          dispatch(deleteDeliverables.success());
          dispatch(fetchScheduledDeliverablesList());
        } else {
          toastMessage.error('Scheduled Deliverable Deleting Was Failed!');
        }
        dispatch(onModalCloseRequest({ id: deleteModalId }));
      } catch (error: any) {
        dispatch(deleteDeliverables.failure());
        const errorMsg =
          error.message ||
          'An error occurs while deleting the Scheduled Deliverables!';
        toastMessage.error(errorMsg);
        dispatch(onModalCloseRequest({ id: deleteModalId }));
        return rejectWithValue(errorMsg);
      }
    },
  );
export const fetchActivityLogs = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'scheduledDeliverables/fetchActivityLogs',
  async (
    { schedule_id }: GetActivityLogsRequestPayload,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(getActivityLogs.request({ schedule_id }));

    try {
      const response = await apiInstance.scheduledDeliverables.getActivityLogs({
        schedule_id,
      });
      dispatch(
        getActivityLogs.success({ data: response.data.data, schedule_id }),
      );
    } catch (error: any) {
      dispatch(getActivityLogs.failure({ schedule_id }));
      return rejectWithValue(
        error.message || 'An error occurred while fetching activity logs.',
      );
    }
  },
);
export const fetchDeliverableReport =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/runDeliverableReports',
    async (
      { isSchedule, data, schedule_id }: RunDeliverableReportRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(runDeliverableReport.request({ isSchedule, data, schedule_id }));

      try {
        const response =
          await apiInstance.runDeliverables.runDeliverableReports(data);
        if (!response.success) {
          toastMessage.error(
            response.error_msg || 'Run Deliverable execution failed',
          );
        }
        // The endpoint starts a background task and returns its id. This tab remembers the id so it
        // still downloads the finished workbook automatically; the download list is refreshed when
        // the run completes, because that is when the S3 record it lists is written.
        const { task_id: taskId, reattached } = (response.data ??
          {}) as StartedTaskResponse;
        if (taskId) {
          rememberStartedTask(taskId);
        }
        toastMessage.info(
          reattached
            ? `Deliverable #${schedule_id} is already being generated — track it in Background tasks.`
            : `Deliverable #${schedule_id} generation started — track it in Background tasks.`,
        );

        dispatch(
          runDeliverableReport.success({
            schedule_id,
          }),
        );
      } catch (error: any) {
        dispatch(runDeliverableReport.failure({ schedule_id }));
        const errorMessage =
          error.message ||
          'An unknown error occurred while running deliverable';
        toastMessage.error(errorMessage);
        return rejectWithValue(errorMessage);
      }
    },
  );
export const fetchClientEmails = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'scheduledDeliverables/fetchClientEmails',
  async (
    { schedule_id }: GetClientEmailsRequestPayload,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(getClientEmails.request({ schedule_id }));

    try {
      const response = await apiInstance.scheduledDeliverables.getClientEmails({
        schedule_id,
      });
      dispatch(
        getClientEmails.success({ data: response.data.data, schedule_id }),
      );
    } catch (error: any) {
      dispatch(getClientEmails.failure({ schedule_id }));
      return rejectWithValue(
        error.message || 'An error occurred while fetching activity logs.',
      );
    }
  },
);
export const fetchSendClientEmails =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchSendClientEmails',
    async (
      { sendEmailPayload, newActivityPayload }: SendClientEmailsRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(
        sendClientEmails.request({ sendEmailPayload, newActivityPayload }),
      );

      try {
        toastMessage.info('Sending Emails...');
        const response =
          await apiInstance.scheduledDeliverables.sendEmailToClient(
            sendEmailPayload,
          );
        toastMessage.success('Emails sent successfully');
        dispatch(sendClientEmails.success());
        if (response.data.success) {
          dispatch(newActivityLog(newActivityPayload));
        }
      } catch (error: any) {
        dispatch(sendClientEmails.failure());
        const errorMessage =
          error.response?.data?.error_msg ||
          'An unknown error occurred while Sending Emails';
        toastMessage.error(errorMessage);
        return rejectWithValue(errorMessage);
      }
    },
  );
export const newActivityLog = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'scheduledDeliverables/newActivityLog',
  async (params: ActivityLogParams, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await apiInstance.scheduledDeliverables.newActivityLog(params);
      dispatch(fetchActivityLogs({ schedule_id: params.schedule_id }));
      return response.data;
    } catch (error: any) {
      dispatch(sendClientEmails.failure());
      const errorMessage =
        error.message || 'An error occurred while creating new activity log.';
      toastMessage.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);
export const fetchReportListDownload =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchReportListDownload',
    async (
      { schedule_id }: GetReportListDownloadRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(getReportListDownload.request({ schedule_id }));

      try {
        const response =
          await apiInstance.scheduledDeliverables.getReportListDownload({
            schedule_id,
          });
        dispatch(
          getReportListDownload.success({
            data: response.data.data,
            schedule_id,
          }),
        );
      } catch (error: any) {
        dispatch(getReportListDownload.failure({ schedule_id }));
        return rejectWithValue(
          error.message || 'An error occurred while fetching Report Download.',
        );
      }
    },
  );
export const fetchDeleteReportDownload =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/deleteReportDownload',
    async (
      {
        schedule_id,
        download_ids,
        modalId,
      }: DeleteReportDownloadRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(deleteReportDownload.request({ schedule_id, download_ids }));
      const modalIdName = isDef(modalId)
        ? modalId
        : `${ConfirmModals.Delete}_${schedule_id}`;

      try {
        toastMessage.info('Deleting Download Reports...');
        const response =
          await apiInstance.scheduledDeliverables.deleteReportDownload({
            schedule_id,
            download_ids,
          });
        if (response.data.success) {
          toastMessage.success(
            'The Download Reports were deleted successfully',
          );
        } else {
          toastMessage.error(
            'An error occurred while deleting the Download Reports.',
          );
        }
        dispatch(deleteReportDownload.success());
        dispatch(onModalCloseRequest({ id: modalIdName }));
        dispatch(
          fetchReportListDownload({
            schedule_id,
          }),
        );
      } catch (error: any) {
        dispatch(deleteReportDownload.failure());
        const errorMessage =
          error.message ||
          'An error occurred while deleting the Download Report.';
        toastMessage.error(errorMessage);
        return rejectWithValue(errorMessage);
      }
    },
  );
export const fetchNewTrades = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'scheduledDeliverables/fetchNewTrades',
  async (
    { schedule_id }: GetReportListDownloadRequestPayload,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(getNewTrades.request({ schedule_id }));

    try {
      const response =
        await apiInstance.scheduledDeliverables.scheduleDeliverableNewTrades({
          schedule_id,
        });
      dispatch(
        getNewTrades.success({
          data: response.data.data,
          schedule_id,
        }),
      );
    } catch (error: any) {
      dispatch(getNewTrades.failure({ schedule_id }));
      return rejectWithValue(
        error.message ||
          'An unknown error occurred while scheduling deliverable',
      );
    }
  },
);
export const fetchCloneScheduledDeliverable =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchCloneScheduledDeliverable',
    async (
      { schedule_id, scheduled_date }: CloneScheduledDeliverablesRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(
        cloneScheduledDeliverable.request({ schedule_id, scheduled_date }),
      );

      try {
        toastMessage.info(`Cloning Scheduled Deliverable #${schedule_id}`);
        const response =
          await apiInstance.scheduledDeliverables.cloneScheduledDeliverable({
            schedule_id: schedule_id,
            scheduled_date: scheduled_date,
          });
        if (response.data.success) {
          toastMessage.success(`Scheduled Deliverable #${schedule_id} Cloned!`);
        } else {
          toastMessage.error('Scheduled Deliverable request to Clone FAILED!');
        }
        dispatch(cloneScheduledDeliverable.success());
        dispatch(fetchScheduledDeliverablesList());
        dispatch(fetchScheduledDeliverable({ schedule_id }));
      } catch (error: any) {
        const errorMessage =
          error.message ||
          'An unknown error occurred while clone scheduling deliverable';
        dispatch(cloneScheduledDeliverable.failure());
        toastMessage.error(errorMessage);
        return rejectWithValue(errorMessage);
      }
    },
  );
export const fetchScheduleDeliverableTransactions =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchScheduleDeliverableTransactions',
    async (
      params: GetScheduledDeliverableTransactionsRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(getScheduledDeliverableTransactions.request(params));

      try {
        //toastMessage.info('Loading transactions...');
        const response =
          await apiInstance.scheduledDeliverables.scheduledDeliverableTransactions(
            params,
          );
        dispatch(
          getScheduledDeliverableTransactions.success({
            data: response.data.data,
          }),
        );
        //toastMessage.success('Transactions loaded successfully');
      } catch (error: any) {
        dispatch(getScheduledDeliverableTransactions.failure());
        const errMsg =
          error.message || 'An error occurred while fetching transactions.';
        toastMessage.error(errMsg);
        return rejectWithValue(errMsg);
      }
    },
  );

export const createUpdateScheduleDeliverable =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/createUpdateScheduleDeliverable',
    async (
      { data }: CreateUpdateScheduledDeliverableRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      const modalId = data.schedule_id
        ? `${ConfirmModals.Edit}_${data.schedule_id}`
        : ConfirmModals.Create;

      dispatch(createUpdateScheduledDeliverable.request({ data }));
      if (data.schedule_id) {
        toastMessage.info(
          `Updating Scheduled Deliverable #${data.schedule_id}`,
        );
      } else {
        toastMessage.info('Creating New Scheduled Deliverable');
      }

      try {
        const response =
          await apiInstance.scheduledDeliverables.scheduleDeliverable(data);
        dispatch(createUpdateScheduledDeliverable.success());
        if (data.schedule_id) {
          toastMessage.success(
            `Scheduled Deliverable #${data.schedule_id} was updated successfully!`,
          );
          dispatch(
            fetchScheduledDeliverable({ schedule_id: data.schedule_id }),
          );
        } else {
          toastMessage.success(
            `Scheduled Deliverable #${response.data?.schedule_id} was created successfully!`,
          );
          dispatch(fetchScheduledDeliverablesList());
        }

        dispatch(onModalCloseRequest({ id: modalId }));
      } catch (error: any) {
        dispatch(createUpdateScheduledDeliverable.failure());
        const errMsg =
          error.message || 'An error occurred while fetching transactions.';
        toastMessage.error(errMsg);
        return rejectWithValue(errMsg);
      }
    },
  );

export const fetchSaveUploadedFiles =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchSaveUploadedFiles',
    async (
      params: fetchUploadedFilesPayload,
      { dispatch, rejectWithValue },
    ) => {
      try {
        const response =
          await apiInstance.scheduledDeliverables.sendUploadedReportsFromS3(
            params,
          );
        dispatch(fetchReportListDownload({ schedule_id: params.schedule_id }));
        if (response.data.success) {
          dispatch(
            newActivityLog({
              schedule_id: params.schedule_id,
              actor: params.created_by,
              message: `${LogMessages.Uploading_deliverable_report}: ${params.files.map((el) => el.download_file_name).join(', ')} `,
            }),
          );
        }
        return response.data;
      } catch (error: any) {
        return rejectWithValue(
          error.message ||
            'An unknown error occurred in fetchSaveUploadedFiles request',
        );
      }
    },
  );

export const fetchScheduledDeliverablesTransactions =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchScheduledDeliverablesTransactions',
    async (
      { trade_id }: GetScheduledDeliverablesTransactionsRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(getScheduledDeliverablesTransactions.request({ trade_id }));

      try {
        const response =
          await apiInstance.scheduledDeliverables.getScheduledDeliverablesTransactions(
            {
              trade_id,
            },
          );

        dispatch(
          getScheduledDeliverablesTransactions.success({
            data: response.data.data,
            trade_id,
          }),
        );
      } catch (error: any) {
        dispatch(getScheduledDeliverablesTransactions.failure({ trade_id }));
        return rejectWithValue(
          error.message ||
            'An error occurred while fetching Scheduled Deliverables Transactions.',
        );
      }
    },
  );

export const fetchScheduledDeliverablesTestReports =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchScheduledDeliverablesTestReports',
    async (
      { schedule_id }: GetScheduledDeliverableRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(getScheduledDeliverablesTestReports.request({ schedule_id }));
      try {
        const response =
          await apiInstance.scheduledDeliverables.getScheduledDeliverablesTestReports(
            {
              schedule_id,
            },
          );
        dispatch(
          getScheduledDeliverablesTestReports.success({
            data: response.data.data,
            schedule_id,
          }),
        );
        toastMessage.success(
          `Test Reports for Scheduled Deliverable #${schedule_id} loaded successfully`,
        );
        return response.data.data;
      } catch (error: any) {
        dispatch(getScheduledDeliverablesTestReports.failure({ schedule_id }));
        const errMsg =
          error.message || 'An error occurred while fetching Test Reports.';
        toastMessage.error(errMsg);
        return rejectWithValue(errMsg);
      }
    },
  );

export const fetchScheduledDeliverablesTestReport =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/fetchScheduledDeliverablesTestReport',
    async (
      { schedule_id, report_id }: { schedule_id: number; report_id: number },
      { dispatch, rejectWithValue },
    ) => {
      try {
        dispatch(
          getScheduledDeliverablesTestReport.request({
            schedule_id,
            report_id,
          }),
        );
        const response =
          await apiInstance.scheduledDeliverables.getScheduledDeliverablesTestReport(
            {
              report_id,
            },
          );
        toastMessage.success(
          `Test Report #${report_id} for Scheduled Deliverable #${schedule_id} loaded successfully`,
        );
        dispatch(
          getScheduledDeliverablesTestReport.success({
            data: response.data.data,
            schedule_id,
            report_id,
          }),
        );
        return response.data.data;
      } catch (error: any) {
        dispatch(
          getScheduledDeliverablesTestReport.failure({
            schedule_id,
            report_id,
          }),
        );
        const errMsg =
          error.message || 'An error occurred while fetching the Test Report.';
        toastMessage.error(errMsg);
        return rejectWithValue(errMsg);
      }
    },
  );

/**
 * Reruns the automated validations for a scheduled deliverable without revaluing.
 * Revaluation is a separate action, triggered from the Revaluation Tool modal.
 */
export const rerunScheduledDeliverablesValidations =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'scheduledDeliverables/rerunValidations',
    async (
      { schedule_id, as_of_date }: RerunValidationsRequestPayload,
      { dispatch, rejectWithValue },
    ) => {
      dispatch(rerunValidations.request({ schedule_id, as_of_date }));
      try {
        const response =
          await apiInstance.scheduledDeliverables.rerunScheduledDeliverablesValidations(
            {
              // the endpoint takes a batch; the dashboard only ever reruns its own schedule
              schedule_ids: [schedule_id],
              as_of_date,
            },
          );
        dispatch(rerunValidations.success({ schedule_id, as_of_date }));
        toastMessage.success('Validations rerun started');
        // pull in the run that was just created
        dispatch(fetchScheduledDeliverablesTestReports({ schedule_id }));
        return response.data;
      } catch (error: any) {
        dispatch(rerunValidations.failure({ schedule_id, as_of_date }));
        const errMsg =
          error.message || 'An error occurred while rerunning the validations.';
        toastMessage.error(errMsg);
        return rejectWithValue(errMsg);
      }
    },
  );
